<script>

import moment from 'moment/moment';
import { mapGetters, mapState } from 'vuex';
import MarkingStatusBadge from '@/components/markings/MarkingStatusBadge.vue';
import Datamatrix from '../../dev/Datamatrix.vue';

export default {
  name: 'MarkingsListItem',
  props: {
    item: Object,
    isCompatibilityView: Boolean,
    isCompatibilityCorrect: Boolean,
    loadingParents: Boolean,
    parentsList: Array,
    hideActions: Boolean,
    disabled: Boolean,
    fullDisabled: Boolean,
    nonInteractive: Boolean,
  },
  components: { MarkingStatusBadge, Datamatrix },
  emits: [
    'select-item',
    'select-item-contents',
  ],
  computed: {
    ...mapGetters('debug', ['debug']),
    ...mapState({
      lines: state => state.lines.list || [],
      labelingPoints: state => state.labelingPoints.list || [],
    }),
    ...mapGetters('packingSettings', [
      'getUnitById',
      'getHigherLevelUnit',
      'getLowerLevelUnit',
      'getUnitToUnitRelation',
    ]),
    ...mapGetters('emptyLabelArchive', [
      'isLabelArchiveEmpty',
      'emptyAmount',
    ]),
    lineName() {
      return this.lines.find(l => l.id === this.item.lineId)?.name || '?';
    },
    labelingPointName() {
      return this.labelingPoints.find(l => l.id === this.item.labelingPointId)?.name || '?';
    },
    itemUnit() {
      return this.getUnitById(this.item.unitId);
    },
    lowerLevelUnit() {
      return this.getLowerLevelUnit(
        this.item.unitId,
        this.item.skuId,
      );
    },
    upperLevelUnit() {
      return this.getHigherLevelUnit(
        this.item.unitId,
        this.item.skuId,
      );
    },
    itemParent() {
      if (!this.item.containers) return {};
      const parentRelation = this.item.containers
        .find(c => c.containerUnitId === this.upperLevelUnit?.id) || {};
      return this.parentsList.find(({ id }) => parentRelation.containerId === id) || {};
    },
    itemChildren() {
      if (!this.item.containedLabels) return [];
      return this.item.containedLabels
        .filter(c => c.unitId === this.lowerLevelUnit?.id);
    },
    maxAmount() {
      if (!this.lowerLevelUnit) return 0;
      const relation = this.getUnitToUnitRelation(
        this.lowerLevelUnit.id,
        this.item.unitId,
        this.item.skuId,
      );
      return relation?.amount || 0;
    },
    lowerUnitChildrenAmount() {
      if (!this.lowerLevelUnit) return 0;
      return this.itemChildren.length - this.emptyLowerChildrenAmount;
    },
    isFull() {
      if (!this.lowerLevelUnit) return false;
      return this.lowerUnitChildrenAmount >= this.maxAmount;
    },
    isDisabled() {
      if (this.fullDisabled) {
        return this.disabled || this.isFull;
      }
      return this.disabled;
    },
    lockedLowerChildrenRelations() {
      return this.itemChildren.filter(l => l.locked)
        .filter(l => !this.isLabelArchiveEmpty(l.id))
        .length;
    },
    emptyLowerChildrenAmount() {
      if (!this.lowerLevelUnit) return 0;
      return this.emptyAmount(this.itemChildren.map(l => l.id));
    },
    notEmptyLowerLevelChildren() {
      return this.itemChildren.filter(l => !this.isLabelArchiveEmpty(l.id));
    },
    shouldBeClosedButHasManualyRemoved() {
      const relations = this.itemChildren.length;
      return this.emptyLowerChildrenAmount > 0 && relations === this.maxAmount;
    },
    statusBadgeVariant() {
      if (this.shouldBeClosedButHasManualyRemoved) return 'danger';
      if (!this.isFull) return 'success';
      if (this.isFull) return 'secondary';
      return 'danger';
    },
  },
  methods: {
    formatDate(date) {
      return moment(date).format('LLL');
    },
    handleClick() {
      if (this.isDisabled) return;
      this.$emit('select-item', this.item.id);
    },
    handleClickOnChildren() {
      this.$emit('select-item-contents', this.item.id);
    },
    handleParentClick() {
      if (!this.itemParent.serialNumber) {
        this.$emit('select-item-contents', this.item.id);
      } else {
        this.$emit('select-item', this.itemParent.id);
      }
    },
    handleOpenById(id) {
      this.$emit('select-item', id);
    },
  },
};
</script>

<template>
  <div
    v-tippy
    class="markings-list-item"
    :class="{
      'disabled': isDisabled,
      'non-interactive': nonInteractive,
    }"
    :content="(itemUnit?.name || '?').toUpperCase() || ''"
    @click.stop="handleClick"
  >
    <div class="markings-list-item-flex">
      <div class="d-flex align-items-center first-column justify-content-start">
        <Datamatrix
          :encode-string="item.scanCode"
        />

        <div class="pl-2">
          <MarkingStatusBadge
            :status="item.labelStatus"
          />

          <div class="number">
            {{ item.serialNumber }}
          </div>
        </div>
      </div>

      <div
        v-if="upperLevelUnit && !isCompatibilityView && !hideActions"
        v-tippy
        class="parent-number"
        :class="{
          'no-parent': !itemParent.serialNumber,
        }"
        :content="(upperLevelUnit?.name || '?').toUpperCase() || ''"
        @click.stop.prevent="handleParentClick"
      >
        <i
          v-if="loadingParents"
          class="fas fa-loader fa-spin"
        />
        <span v-else>
          <i
            v-if="itemParent.serialNumber"
            class="fas fa-arrow-up-wide-short"
          />
          {{ itemParent.serialNumber || $t('markings.noConnectionToParentElement') }}
        </span>
      </div>

      <div
        v-if="lowerLevelUnit && !isCompatibilityView"
        v-tippy
        class="children-numbers"
        :content="(lowerLevelUnit?.name || '?').toUpperCase()"
        @click.stop.prevent="handleClickOnChildren"
      >
        <BBadge
          v-if="lowerLevelUnit"
          class="ml-1"
          :variant="statusBadgeVariant"
        >
          <i
            v-if="shouldBeClosedButHasManualyRemoved"
            class="fas fa-hand-holding-box mr-1 fa-fw"
          />
          <i
            v-if="!isFull"
            class="fas fa-box-open fa-fw"
          />
          <i
            v-else
            class="fas fa-box fa-fw"
          />

          <span class="ml-1">
            {{ lowerUnitChildrenAmount }}/{{ maxAmount }}
          </span>
        </BBadge>
      </div>

      <div class="date">
        <i class="far fa-calendar" />

        {{ formatDate(item.created) }}
      </div>

      <div class="order-id">
        <span class="label">
          Order ID:
        </span>

        {{ item.orderId }}
      </div>

      <div class="line">
        {{ lineName }}

        <span class="subline-name">
          /
          {{ labelingPointName }}
        </span>
      </div>
    </div>
    <div
      v-if="isCompatibilityView"
      class="compatibility-row"
    >
      <div class="compatibility-parent-box">
        <i class="fas fa-arrow-up-wide-short" />
        <div
          :key="itemParent.id"
          class="number"
          :class="{
            'the-same-parent': isCompatibilityCorrect,
          }"
        >
          <span
            v-if="upperLevelUnit"
            class="unit"
          >
            Przypisany do
            {{ upperLevelUnit?.name.toUpperCase() || '' }}:
          </span>
          {{ itemParent.serialNumber || $t('markings.noConnectionToParentElement') }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.markings-list-item {
  border: 1px solid #F0F1F3;
  background-color: white;
  padding: 8px 12px;
  font-size: 13px;
  border-radius: 6px;
  cursor: pointer;

  @media (max-width: 1100px) {
    padding: 15px 20px;
    font-size: 11px;
  }

  .markings-list-item-flex {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .first-column {
      width: 200px;
    }

    .second-column {
      width: 150px;
    }
  }

  .compatibility-row {
    margin-top: 10px;

    .compatibility-parent-box {
      font-size: 13px;
      display: flex;
      align-items: center;

      i {
        color: #595959;
      }

      .number {
        color: black;
        font-weight: 500;
        margin-left: 10px;
        padding: 5px 15px;
        background-color: rgba(#ed3028, 0.2);
        border-radius: 50px;
        transition: 0.3s all;

        .unit {
          font-weight: 400;
          color: #424242;
          font-size: 11px;
          margin-right: 5px;
        }

        &.the-same-parent {
          background-color: rgba(#7CB92D, 0.2);

          &:hover {
            background-color: rgba(#7CB92D, 0.5);
          }
        }
      }
    }
  }

  &:hover:not(.disabled) {
    background-color: #fefefe;
    border: 1px solid #7CB92D;
    box-shadow: 0 1px 2px 0 rgba(#7CB92D, 0.25);
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.non-interactive {
    cursor: default;

    > * {
      cursor: default !important;
    }
  }

  .number {
    font-weight: 500;
  }

  .children-numbers {
    font-size: 0.9rem;
  }

  .children-numbers, .parent-number {
    font-weight: 500;
    min-width: 100px;
    max-width: 100px;
    transition: 0.2s all;
    border-radius: 6px;
    display: inline-block;
    text-align: center;

    &:hover {
      background-color: #c2c2c2;
      padding: 3px;
    }

    &.parent-number {
      min-width: 170px;
      max-width: 170px;

      &.no-parent {
        font-size: 11px;
        font-weight: 400;
        color: #8A8A8A;
      }
    }

    i {
      margin-right: 4px;
    }
  }

  .date {
    font-size: 11px;
    color: #8A8A8A;

    i {
      margin-right: 2px;
    }
  }

  .order-id {
    font-size: 11px;
    color: #7E7E7E;

    .label {
      font-weight: 500;
    }
  }

  .line {
    font-size: 13px;
    color: black;
    font-weight: 500;

    @media (max-width: 1100px) {
      font-size: 10px;
    }

    .subline-name {
      font-size: 11px;
      font-weight: 400;
      color: #7E7E7E;
    }
  }

  .item-actions {
    display: flex;
    gap: 12px;

    .item-action {
      width: 32px;
      height: 32px;
      font-size: 15px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 30px;
      color: #585858;
      background-color: #EFEFEF;
      cursor: pointer;

      &:hover {
        background-color: #E0E0E0;
      }
    }
  }
}
</style>
